import { useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  CreateBase,
  Form,
  required,
  useGetList,
  useNotify,
} from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { RadioButtonGroupInput } from "@/components/admin/radio-button-group-input";
import { TextInput } from "@/components/admin/text-input";

import { FormToolbar } from "../layout/FormToolbar";
import type { DealStageConfig } from "../types";
import {
  deriveStageKey,
  NEW_STAGE_PLACEMENT_CHOICES,
  nextStagePosition,
  STAGE_LABEL_MAX_LENGTH,
  toStageConfigErrorMessage,
  validateStageLabel,
} from "./stageConfigForm";

/**
 * Almikia Epic EP (TASK-105): "Nueva etapa" -- direccion picks a label and a
 * placement (Tablero or Estado especial, never Bandeja: `deriveStageKey`'s
 * caller here never offers 'inbox', mirroring the DB guard that rejects it
 * outright). The new stage is appended LAST in its placement group
 * (`nextStagePosition`) with a `key` derived from the label
 * (`deriveStageKey`) -- the key is never shown or editable again afterwards.
 */
export const DealStageConfigCreate = () => (
  <CanAccess
    resource="deal_stage_configs"
    action="create"
    accessDenied={<Navigate to="/" replace />}
  >
    <DealStageConfigCreateContent />
  </CanAccess>
);

const DealStageConfigCreateContent = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data: configs } = useGetList<DealStageConfig>("deal_stage_configs", {
    pagination: { page: 1, perPage: 100 },
  });

  if (!configs) return null;

  const activeLabels = configs
    .filter((config) => config.archived_at == null)
    .map((config) => config.label);
  const existingKeys = configs.map((config) => config.key);

  const transform = (data: {
    label: string;
    placement: "kanban" | "special";
  }) => {
    const label = data.label.trim();
    return {
      label,
      placement: data.placement,
      position: nextStagePosition(configs, data.placement),
      key: deriveStageKey(label, existingKeys),
      target_hours: null,
      required_activities: [],
    };
  };

  return (
    <CreateBase
      redirect="list"
      transform={transform}
      mutationOptions={{
        onSettled: () => {
          // Almikia Epic EP: the effective-stages cache read by
          // `useConfigurationLoader` is keyed independently of react-admin's
          // default `[resource, "getList"]` invalidation, so a new stage
          // stays invisible on the board/picker until this refetch fires.
          // Uses `onSettled` (not `onSuccess`) because CreateBase's own
          // `onSuccess` drives the "created" notification and redirect --
          // overriding it there would strand the user on the form.
          queryClient.invalidateQueries({
            queryKey: ["deal_stage_configs", "effective"],
          });
        },
        onError: (error) =>
          notify(toStageConfigErrorMessage(error), { type: "error" }),
      }}
    >
      <div className="mt-2 max-w-lg mx-auto">
        <Card>
          <CardContent>
            <Form
              defaultValues={{ placement: "kanban" }}
              className="space-y-4 pt-4"
            >
              <TextInput
                source="label"
                label="resources.deal_stage_configs.fields.label"
                validate={[
                  required(),
                  (value: string) => validateStageLabel(value, activeLabels),
                ]}
                maxLength={STAGE_LABEL_MAX_LENGTH}
                helperText={false}
              />
              <RadioButtonGroupInput
                source="placement"
                label="resources.deal_stage_configs.fields.placement"
                choices={NEW_STAGE_PLACEMENT_CHOICES}
                validate={required()}
                helperText="resources.deal_stage_configs.new_stage.placement_help"
              />
              <FormToolbar />
            </Form>
          </CardContent>
        </Card>
      </div>
    </CreateBase>
  );
};
